/**
 * Relation math utilities — pure, DOM-free, no Math.random().
 *
 * Elements are represented as strings. Relations are arrays of Pair.
 * Internal membership checks use a Set of "a\x00b" keys for O(1) lookup.
 */

/** An ordered pair of element ids. */
export type Pair = readonly [string, string];

/** Relation properties supported by violatingPairs. */
export type RelationProperty = 'reflexive' | 'symmetric' | 'antisymmetric' | 'transitive';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Build an O(1) membership set from a relation. Key format: `a\x00b`. */
function makeKey(a: string, b: string): string {
  return `${a}\x00${b}`;
}

function makeKeySet(r: readonly Pair[]): Set<string> {
  const s = new Set<string>();
  for (const [a, b] of r) {
    s.add(makeKey(a, b));
  }
  return s;
}

// ---------------------------------------------------------------------------
// Property predicates
// ---------------------------------------------------------------------------

/**
 * Returns true iff every element of `on` has a reflexive pair in `r`.
 * i.e. ∀x∈on: (x,x)∈r.
 */
export function isReflexive(r: readonly Pair[], on: readonly string[]): boolean {
  const keys = makeKeySet(r);
  return on.every((x) => keys.has(makeKey(x, x)));
}

/**
 * Returns true iff `r` is symmetric: (a,b)∈r ⇒ (b,a)∈r.
 */
export function isSymmetric(r: readonly Pair[]): boolean {
  const keys = makeKeySet(r);
  return r.every(([a, b]) => keys.has(makeKey(b, a)));
}

/**
 * Returns true iff `r` is antisymmetric: (a,b)∈r ∧ (b,a)∈r ⇒ a=b.
 */
export function isAntisymmetric(r: readonly Pair[]): boolean {
  const keys = makeKeySet(r);
  return r.every(([a, b]) => a === b || !keys.has(makeKey(b, a)));
}

/**
 * Returns true iff `r` is transitive: (a,b)∈r ∧ (b,c)∈r ⇒ (a,c)∈r.
 */
export function isTransitive(r: readonly Pair[]): boolean {
  const keys = makeKeySet(r);
  for (const [a, b] of r) {
    for (const [b2, c] of r) {
      if (b === b2 && !keys.has(makeKey(a, c))) {
        return false;
      }
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// Matrix
// ---------------------------------------------------------------------------

/**
 * Build a boolean matrix where `matrix[i][j]` is true iff `(rows[i], cols[j]) ∈ r`.
 */
export function relationMatrix(
  r: readonly Pair[],
  rows: readonly string[],
  cols: readonly string[],
): boolean[][] {
  const keys = makeKeySet(r);
  return rows.map((row) => cols.map((col) => keys.has(makeKey(row, col))));
}

// ---------------------------------------------------------------------------
// Violation witnesses
// ---------------------------------------------------------------------------

/**
 * Returns the witnessing / missing pairs for a FAILED property.
 *
 * - `reflexive`:     missing (x,x) for x∈on not already in r.
 * - `symmetric`:     missing reverse (b,a) for (a,b)∈r whose reverse is absent.
 * - `antisymmetric`: both (a,b) and (b,a) for each off-diagonal symmetric pair.
 * - `transitive`:    missing (a,c) for (a,b),(b,c)∈r with (a,c)∉r (deduped).
 *
 * Returns [] when the property holds.
 */
export function violatingPairs(
  prop: RelationProperty,
  r: readonly Pair[],
  on: readonly string[],
): Pair[] {
  const keys = makeKeySet(r);

  switch (prop) {
    case 'reflexive': {
      return on.filter((x) => !keys.has(makeKey(x, x))).map((x): Pair => [x, x]);
    }

    case 'symmetric': {
      return r.filter(([a, b]) => !keys.has(makeKey(b, a))).map(([a, b]): Pair => [b, a]);
    }

    case 'antisymmetric': {
      const seen = new Set<string>();
      const result: Pair[] = [];
      for (const [a, b] of r) {
        if (a !== b && keys.has(makeKey(b, a))) {
          // Report both the pair and its reverse, deduped across iterations
          const fwd = makeKey(a, b);
          const rev = makeKey(b, a);
          if (!seen.has(fwd)) {
            seen.add(fwd);
            result.push([a, b]);
          }
          if (!seen.has(rev)) {
            seen.add(rev);
            result.push([b, a]);
          }
        }
      }
      return result;
    }

    case 'transitive': {
      const seen = new Set<string>();
      const result: Pair[] = [];
      for (const [a, b] of r) {
        for (const [b2, c] of r) {
          if (b === b2 && !keys.has(makeKey(a, c))) {
            const k = makeKey(a, c);
            if (!seen.has(k)) {
              seen.add(k);
              result.push([a, c]);
            }
          }
        }
      }
      return result;
    }
  }
}
